import { NotificationsProdiver } from '@components/Notifications/NotificationsProvider';
import { TooltipProvider } from '@components/shadcn/components/ui/tooltip';
import { SystemLanguageContextProvider } from '@contexts/SystemLanguageContext';
import { initCWElements } from '@cw-elements/config';
import { initMfes } from '@cw-elements/mfe-setup/init';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { IndexedDBPromiseAPI } from '@utils/indexeddb.utils';
import { queriesToCache } from '@utils/persister.utils';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { UnitSystemContextProvider } from './contexts/UnitSystemContext';
import { CropwiseElementAxiosInstance } from './core/api';
import { asyncPersister, queryClient } from './core/api/query';
import { persistor, store } from './redux/store';
import './sass/entry-vendor.scss';
import './sass/index.scss';
import { Router } from './screens';
import * as serviceWorker from './serviceWorker';
import { AntdLocalProviderWrapper } from './utils/AntdLocalProviderWrapper';
import './utils/translations';

const tokens = JSON.parse(localStorage.getItem('tokens') ?? 'null');
initCWElements({
  environment: import.meta.env.VITE_APP_CROPWISE_ELEMENTS_ENV,
  hostAppId: import.meta.env.VITE_APP_IMAGERY_APPID,
  axiosInstance: CropwiseElementAxiosInstance,
  token: tokens?.access_token,
  googleSatelliteApiKey: import.meta.env.VITE_APP_GOOGLE_TILES_KEY,
});

const container = document.getElementById('root');
const root = createRoot(container!);

(async () => {
  const [_, __, LDProvider] = await Promise.all([
    initMfes({
      'ai-chatbot': `${import.meta.env.VITE_CW_ELEMENTS_MFES}/ai-chatbot`,
      'connect-files': `${import.meta.env.VITE_CW_ELEMENTS_MFES}/connect-files`,
      'quota-mgt': `${import.meta.env.VITE_CW_ELEMENTS_MFES}/quota-mgt`,
      'entities-hall': `${import.meta.env.VITE_CW_ELEMENTS_MFES}/entities-hall`,
      fields: `${import.meta.env.VITE_CW_ELEMENTS_MFES}/fields`,
      'members-mgt': `${import.meta.env.VITE_CW_ELEMENTS_MFES}/members-mgt`,
      subscriptions: `${import.meta.env.VITE_CW_ELEMENTS_MFES}/subscriptions`,
      'topbar-navigation': `${import.meta.env.VITE_CW_ELEMENTS_MFES}/topbar-navigation`,
    }),
    IndexedDBPromiseAPI.initializeIDB(),
    asyncWithLDProvider({
      clientSideID: import.meta.env.VITE_APP_LAUNCHDARKLY_CLIENT_SIDE_ID,
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
    }),
  ]);

  root.render(
    <LDProvider>
      <Provider store={store}>
        <SystemLanguageContextProvider>
          <AntdLocalProviderWrapper>
            <UnitSystemContextProvider>
              <PersistGate loading={null} persistor={persistor}>
                <PersistQueryClientProvider
                  client={queryClient}
                  persistOptions={{
                    persister: asyncPersister,
                    dehydrateOptions: {
                      shouldDehydrateQuery: (query) => {
                        const [key] = query.queryKey;
                        return queriesToCache.includes(key as string);
                      },
                    },
                  }}
                >
                  <NotificationsProdiver>
                    <TooltipProvider delayDuration={0} skipDelayDuration={50}>
                      <Router />
                    </TooltipProvider>
                  </NotificationsProdiver>
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    toggleButtonProps={{ style: { bottom: '52px' } }}
                  />
                </PersistQueryClientProvider>
              </PersistGate>
            </UnitSystemContextProvider>
          </AntdLocalProviderWrapper>
        </SystemLanguageContextProvider>
      </Provider>
    </LDProvider>
  );
})();

serviceWorker.unregister();
